const _excluded = ["isHorizontal", "indicator", "alignIndicator", "className"];
function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _slicedToArray(r, e) {
  return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest();
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray(r, a) {
  if (r) {
    if ("string" == typeof r) return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0;
  }
}
function _arrayLikeToArray(r, a) {
  (null == a || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e];
  return n;
}
function _iterableToArrayLimit(r, l) {
  var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = !0,
      o = !1;
    try {
      if (i = (t = t.call(r)).next, 0 === l) {
        if (Object(t) !== t) return;
        f = !1;
      } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0);
    } catch (r) {
      o = !0, n = r;
    } finally {
      try {
        if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return;
      } finally {
        if (o) throw n;
      }
    }
    return a;
  }
}
function _arrayWithHoles(r) {
  if (Array.isArray(r)) return r;
}
function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var n = Object.getOwnPropertySymbols(e);
    for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (e.indexOf(n) >= 0) continue;
    t[n] = r[n];
  }
  return t;
}
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useCallback, useMemo, useRef, forwardRef } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiI18n } from '../i18n';
import { useEuiMemoizedStyles, useGeneratedHtmlId } from '../../services';
import { useEuiResizableContainerContext } from './context';
import { euiResizableButtonStyles } from './resizable_button.styles';
/**
 * A generic button for indicating/facilitating resizable content,
 * usable outside of the EuiResizableContainer context
 */
export var EuiResizableButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var isHorizontal = _ref.isHorizontal,
    _ref$indicator = _ref.indicator,
    indicator = _ref$indicator === void 0 ? 'handle' : _ref$indicator,
    _ref$alignIndicator = _ref.alignIndicator,
    alignIndicator = _ref$alignIndicator === void 0 ? 'center' : _ref$alignIndicator,
    className = _ref.className,
    rest = _objectWithoutProperties(_ref, _excluded);
  var classes = classNames('euiResizableButton', className);
  var resizeDirection = isHorizontal ? 'horizontal' : 'vertical';
  var styles = useEuiMemoizedStyles(euiResizableButtonStyles);
  var cssStyles = [styles.euiResizableButton, styles[indicator], styles["".concat(indicator, "Direction")][resizeDirection], styles[resizeDirection], indicator === 'handle' && styles.alignIndicator[alignIndicator]];
  return ___EmotionJSX(EuiI18n, {
    tokens: ['euiResizableButton.horizontalResizerAriaLabel', 'euiResizableButton.verticalResizerAriaLabel'],
    defaults: ['Press the left or right arrow keys to adjust panels size', 'Press the up or down arrow keys to adjust panels size']
  }, function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
      horizontalResizerAriaLabel = _ref3[0],
      verticalResizerAriaLabel = _ref3[1];
    return ___EmotionJSX("button", _extends({
      ref: ref,
      "aria-label": isHorizontal ? horizontalResizerAriaLabel : verticalResizerAriaLabel,
      className: classes,
      css: cssStyles,
      "data-test-subj": "euiResizableButton",
      type: "button"
    }, rest));
  });
});
EuiResizableButton.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * Defaults to displaying a resizer for vertical (y-axis) resizing.
       * Set to `true` to display a resizer for horizontal (x-axis) resizing.
       */
  isHorizontal: PropTypes.bool,
  /**
       * By default, EuiResizableButton will show a grab handle to indicate resizability.
       * This indicator can be optionally hidden to show a plain border instead.
       */
  indicator: PropTypes.oneOf(["handle", "border"]),
  /**
       * Allows customizing the alignment of grab `handle` indicators (does nothing for
       * border indicators). Defaults to `center`, but consider using `start` for extremely
       * tall content that scrolls off-screen
       */
  alignIndicator: PropTypes.oneOf(["center", "start", "end"]),
  /**
       * When disabled, the resizer button will be completely hidden
       */
  disabled: PropTypes.bool
};
EuiResizableButton.displayName = 'EuiResizableButton';

/**
 * Resizer button specific to controlled EuiResizableContainer usage
 */

export var EuiResizableButtonControlled = ({
  registration,
  id,
  disabled,
  onFocus,
  ...rest
}) => {
  const resizerId = useGeneratedHtmlId({
    prefix: 'resizable-button',
    conditionalId: id
  });
  const {
    registry: {
      resizers
    } = {
      resizers: {}
    }
  } = useEuiResizableContainerContext();
  const isDisabled = useMemo(() => disabled || resizers[resizerId]?.isDisabled, [resizers, resizerId, disabled]);
  const previousRef = useRef();
  const onRef = useCallback(ref => {
    if (!registration) return;
    if (ref) {
      previousRef.current = ref;
      registration.register({
        id: resizerId,
        ref,
        isFocused: false,
        isDisabled: disabled || false
      });
    } else {
      if (previousRef.current != null) {
        registration.deregister(resizerId);
        previousRef.current = undefined;
      }
    }
  }, [registration, resizerId, disabled]);
  return <EuiResizableButton id={resizerId} ref={onRef} disabled={isDisabled} onClick={e => e.currentTarget.focus()} onFocus={() => onFocus?.(resizerId)} {...rest} />;
};
export const euiResizableButtonWithControls = controls => props => <EuiResizableButtonControlled {...controls} {...props} />;
try {
  EuiResizableButton.__docgenInfo = {
    tags: {},
    filePath: '/app/packages/eui/src/components/resizable_container/resizable_button.tsx',
    description: 'A generic button for indicating/facilitating resizable content,\n' + 'usable outside of the EuiResizableContainer context',
    displayName: 'EuiResizableButton',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'eui/node_modules/@emotion/react/types/css-prop.d.ts',
          name: 'Attributes'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      isHorizontal: {
        defaultValue: null,
        description: 'Defaults to displaying a resizer for vertical (y-axis) resizing.\n' + 'Set to `true` to display a resizer for horizontal (x-axis) resizing.',
        name: 'isHorizontal',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/resizable_container/resizable_button.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      indicator: {
        defaultValue: {
          value: 'handle'
        },
        description: 'By default, EuiResizableButton will show a grab handle to indicate resizability.\n' + 'This indicator can be optionally hidden to show a plain border instead.',
        name: 'indicator',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/resizable_container/resizable_button.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"border" | "handle"',
          value: [{
            value: '"border"'
          }, {
            value: '"handle"'
          }]
        }
      },
      alignIndicator: {
        defaultValue: {
          value: 'center'
        },
        description: 'Allows customizing the alignment of grab `handle` indicators (does nothing for\n' + 'border indicators). Defaults to `center`, but consider using `start` for extremely\n' + 'tall content that scrolls off-screen',
        name: 'alignIndicator',
        parent: undefined,
        declarations: [{
          fileName: 'eui/src/components/resizable_container/resizable_button.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"center" | "end" | "start"',
          value: [{
            value: '"center"'
          }, {
            value: '"end"'
          }, {
            value: '"start"'
          }]
        }
      },
      ref: {
        defaultValue: null,
        description: 'Allows getting a ref to the component instance.\n' + 'Once the component unmounts, React will set `ref.current` to `null`\n' + '(or call the ref with `null` if you passed a callback ref).\n' + '@see {@link https ://react.dev/learn/referencing-values-with-refs#refs-and-the-dom React Docs}',
        name: 'ref',
        parent: {
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'eui/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'LegacyRef<HTMLButtonElement>'
        }
      }
    },
    extendedInterfaces: ['ButtonHTMLAttributes', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps', 'Attributes']
  };
} catch (e) {}